import { z } from "zod";
import { IdentifiedOrDeletedAuthor } from "./author";
import { IsoDateString } from "./isoDate";
import { TipTapDocSchema } from "./tiptapDoc";
import { UrlSearchText } from "./urlSearch";
import { User } from "./user";

export const VendorVoteType = z.enum(["LIKE", "UNLIKE"]);

const UserVoteData = z
    .object({
        vote: VendorVoteType,
        id: z.number().nullish(),
    })
    .optional()
    .nullish();

export const Vendor = z.object({
    id: z.number(),
    name: z.string(),
    isApproved: z.boolean(),
    website: z.string().url(),
    contactName: z.string().optional().nullish(),
    contactEmail: z
        .email("Invalid email address")
        .or(z.literal("").transform(() => null))
        .optional()
        .nullable(),
    contactAddress: z.string().optional().nullish(),
    category: z.string().optional().nullish(),
    notes: z.string().optional().nullish(),
    notesContentJson: TipTapDocSchema.nullable().optional(),
    userCanEdit: z.boolean(),
    canApprove: z.boolean(),
    // Who submitted the vendor: the chip union (PRTL-1384), was a flat PublicUser.
    author: IdentifiedOrDeletedAuthor,
    createdAt: IsoDateString,
    rating: z.number().optional().nullish(),
    commentCount: z.number(),
    likesVotesCount: z.number(),
    unlikesVotesCount: z.number(),
    userVoteData: UserVoteData,
});

export const VendorListingEntry = Vendor;

export const NewVendor = z.object({
    name: z.string().trim().min(1, { message: "Please enter the company name." }),

    website: z
        .string()
        .min(1, { message: "Please provide a website." })
        .refine(
            (val) => {
                try {
                    const url = new URL(val.startsWith("http") ? val : `https://${val}`);
                    return /\.[a-z]{2,}$/i.test(url.hostname); // .com, .net etc.
                } catch {
                    return false;
                }
            },
            {
                message: "e.g. example.com, www.example.com, https://example.com",
            },
        ),

    contactName: z.string().optional().nullish(),

    contactEmail: z
        .string()
        .optional()
        .refine((val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
            message: "eg. user@example.com",
        }),

    contactAddress: z.string().optional().nullish(),

    category: z.string().min(1, { message: "Please select a category." }),

    /** Legacy plaintext input — kept so navigator's vendor form keeps working
     *  during the migration. Web sends `notesContentJson` instead. The server
     *  prefers `notesContentJson` when both are provided. */
    notes: z.string().optional().nullish(),
    notesContentJson: TipTapDocSchema.nullable().optional(),

    rating: z.number().optional().nullish(),
});

export const VendorList = {
    input: z.object({
        search: z.string().optional(),
        categories: z.array(z.string()).optional(),
        /** PRTL-917: only vendors the requesting user has favorited. */
        favoritesOnly: z.boolean().optional(),
        page: z.number().min(0).optional(),
        pageSize: z.number().min(1).max(30).optional(),
        sortBy: z
            .enum(["name", "contactName", "contactEmail", "category", "website", "createdAt"])
            .optional(),
        sort: z.enum(["asc", "desc"]).optional(),
    }),
    output: z.object({
        vendors: z.array(VendorListingEntry),
        totalFound: z.number(),
        total: z.number(),
    }),
};

/**
 * PRTL-1487: the `/vendors` route's search params. The same fields as
 * `VendorList.input`, but tolerant of whatever a hand-edited URL or old bookmark
 * carries: `?search=2024` (parsed by the router as a number) is read as "2024",
 * and a malformed value for any other field is dropped rather than throwing.
 */
export const VendorListRouteSearch = z.object({
    search: UrlSearchText,
    categories: VendorList.input.shape.categories.catch(undefined),
    favoritesOnly: VendorList.input.shape.favoritesOnly.catch(undefined),
    page: VendorList.input.shape.page.catch(undefined),
    pageSize: VendorList.input.shape.pageSize.catch(undefined),
    sortBy: VendorList.input.shape.sortBy.catch(undefined),
    sort: VendorList.input.shape.sort.catch(undefined),
});
